var frameThrottle = require("enyo-perf/utils").frameThrottle,
    kind          = require("enyo/kind");

require("enyo-ui");

/**
 * [ScrollThumbDragSupport]{@link enyo-ui.ScrollThumbDragSupport} makes
 * [ScrollThumb]{@link enyo-ui.ScrollThumb} draggable and uses the drag gesture
 * to scroll it's [Scroller]{@link enyo-ui.Scroller}.
 *
 * @mixin enyo-ui.ScrollThumbDragSupport
 * @public
 */
module.exports = {

    /**
     * @private
     */
    name: "enyo-ui.ScrollThumbDragSupport",

    /**
     * Whether the thumb is currently being dragged.
     *
     * @type     {boolean}
     * @readonly
     * @memberof enyo-ui.ScrollThumbDragSupport
     * @public
     */
    dragging: false,

    /**
     * Flag whether dragging the thumb should scroll the scroller.
     *
     * @type     {boolean}
     * @default  true
     * @memberof enyo-ui.ScrollThumbDragSupport
     * @public
     */
    shouldScrollOnDrag: true,

    /**
     * Memorized value of `event.pageX` from drag event.
     *
     * @type     {?number}
     * @memberof enyo-ui.ScrollThumbDragSupport
     * @private
     */
    dragPageX: null,

    /**
     * Memorized value of `event.pageY` from drag event.
     *
     * @type     {?number}
     * @memberof enyo-ui.ScrollThumbDragSupport
     * @private
     */
    dragPageY: null,

    /**
     * The multiplier that converts horizontal drag delta to scroll delta.
     *
     * @type     {?number}
     * @memberof enyo-ui.ScrollThumbDragSupport
     * @private
     */
    dragMultiplierX: null,

    /**
     * The multiplier that converts vertical drag delta to scroll delta.
     *
     * @type     {?number}
     * @memberof enyo-ui.ScrollThumbDragSupport
     * @private
     */
    dragMultiplierY: null,

    /**
     * @memberof enyo-ui.ScrollThumbDragSupport
     * @private
     */
    handlers: {
        onmousedown:  "onMouseDown",
        ondragstart:  "onDragStart",
        ondrag:       "onDrag",
        ondragfinish: "onDragFinish"
    },

    /**
     * Add an invisible element to possibly provide larger hit surface for drag
     * events.
     *
     * @memberof enyo-ui.ScrollThumbDragSupport
     * @method
     * @private
     */
    initComponents: kind.inherit(function(sup) {
        return function() {
            this.createComponent({
                name:    "dragHelper",
                classes: "enyo-ui-scroll-thumb__drag-helper"
            });
            sup.apply(this, arguments);
        };
    }),

    /**
     * @memberof enyo-ui.ScrollThumbDragSupport
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.observe("scroller", "onDragFinish");
            this.observe("track", "onDragFinish");
            this.observe("axis", "onDragFinish");
            this._onDrag = frameThrottle(this.bindSafely(this._onDrag));
            sup.apply(this, arguments);
        };
    }),

    /**
     * For some reason dragging the thumb sometimes makes the scroller scroll
     * horizontally when dragging a vertical ScrollThumb. This horizontal
     * scrolling is not part of the drag gesture implemented by this mixin, and
     * I have no idea where it comes from. Luckily it can be prevented by
     * defaultPreventing the mousedown event.
     *
     * @param  {Control}  sender  The sender of the event
     * @param  {Object}   event   The event object
     * @private
     */
    onMouseDown: function(sender, event) {
        event.preventDefault();
    },

    /**
     * Start a dragging interaction based on `ondragstart` event.
     *
     * @param    {Control}  sender  The sender of the event
     * @param    {Object}   event   The event object
     * @returns  {boolean}  Prevent propagation
     * @memberof enyo-ui.ScrollThumbDragSupport
     * @private
     */
    onDragStart: function(sender, event) {
        var scroller    = this.scroller,
            trackSize   = this.trackSize,
            axis        = this.axis,
            scrollerDimensions,
            scrollerWidth,
            scrollerHeight,
            trackWidth,
            trackHeight,
            multiplierX = 0,
            multiplierY = 0;

        if (this.dragging || !this.shouldScrollOnDrag || !scroller || !trackSize) {
            return false;
        }

        this.set("dragging", true);
        scrollerDimensions = scroller.dimensions;

        // Horizontal drag
        if (axis === "horizontal" || axis === "both") {
            scrollerWidth = scrollerDimensions.contentWidth;
            trackWidth    = trackSize.width;

            // Multiplier that converts drag delta to scroll delta.
            if (scrollerWidth > 0 && trackWidth > 0) {
                multiplierX = (scrollerWidth / trackWidth);
            }
        }

        // Vertical drag
        if (axis === "vertical" || axis === "both") {
            scrollerHeight = scrollerDimensions.contentHeight;
            trackHeight    = trackSize.height;

            // Multiplier that converts drag delta to scroll delta.
            if (scrollerHeight > 0 && trackHeight > 0) {
                multiplierY = (scrollerHeight / trackHeight);
            }
        }

        // Set state
        this.dragMultiplierX = multiplierX;
        this.dragMultiplierY = multiplierY;
        this.dragPageX       = event.pageX;
        this.dragPageY       = event.pageY;

        return true;
    },

    /**
     * Captures the drag event and executs the throttled handler as needed.
     *
     * @param    {Control}  sender  The sender of the event
     * @param    {Object}   event   The event object
     * @returns  {boolean}  Prevent propagation
     * @memberof enyo-ui.ScrollThumbDragSupport
     * @private
     */
    onDrag: function(sender, event) {
        if (!this.dragging) {
            return false;
        }

        this._onDrag(sender, event);

        return true;
    },

    /**
     * Drags the scroller based on the drag delta since last `ondrag` event.
     *
     * @param    {Control}  sender  The sender of the event
     * @param    {Object}   event   The event object
     * @memberof enyo-ui.ScrollThumbDragSupport
     * @private
     */
    _onDrag: function(sender, event) {
        var scroller    = this.scroller,
            axis        = this.axis,
            pageX       = this.dragPageX,
            pageY       = this.dragPageY,
            multiplierX = this.dragMultiplierX,
            multiplierY = this.dragMultiplierY,
            deltaX      = 0,
            deltaY      = 0;

        // Calculate horizontal drag delta
        if (axis === "horizontal" || axis === "both") {
            deltaX = (event.pageX - pageX) * multiplierX;
        }

        // Calculate vertical drag delta
        if (axis === "vertical" || axis === "both") {
            deltaY = (event.pageY - pageY) * multiplierY;
        }

        // Memorize drag coordinates
        this.dragPageX = event.pageX;
        this.dragPageY = event.pageY;

        // Apply drag delta to scroller
        scroller.snapBy(deltaX, deltaY);
    },

    /**
     * Stop dragging interaction.
     *
     * @param    {Control}  sender  The sender of the event
     * @param    {Object}   event   The event object
     * @returns  {boolean}  Prevent propagation
     * @memberof enyo-ui.ScrollThumbDragSupport
     * @private
     */
    onDragFinish: function(sender, event) {
        if (!this.dragging) {
            return false;
        }

        // Prevent any throttled drag handlers from executing
        this._onDrag.cancel();

        // Prevent tap if possible
        if (event && event.preventTap) {
            event.preventTap();
        }

        // Reset state
        this.dragMultiplierX = null;
        this.dragMultiplierY = null;
        this.dragPageX       = null;
        this.dragPageY       = null;

        // No longer dragging
        this.set("dragging", false);

        return true;
    },

    /**
     * Determines if control is dragging.
     *
     * @return {boolean}  `true` if dragging, `false` otherwise.
     * @memberof enyo-ui.ScrollThumbDragSupport
     * @public
     */
    isDragging: function() {
        return this.dragging;
    },

    /**
     * Stop drag gesture when node gets removed from DOM.
     *
     * @memberof enyo-ui.ScrollThumbDragSupport
     * @method
     * @private
     */
    removeNodeFromDom: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.onDragFinish();
        };
    }),

    /**
     * Stop drag gesture when control is no longer generated (implying it does
     * not have a DOM Node anymore).
     *
     * @memberof enyo-ui.ScrollThumbDragSupport
     * @method
     * @private
     */
    generatedChanged: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);

            if (!this.generated && this.dragging) {
                this.onDragFinish();
            }
        };
    })

};
